<template>
  <button @click="exportClientData" class="btn btn-sm btn-info mr-1 mb-1">
    <i class="fa fa-download"></i>
    {{ $t('export') }}
    <b-modal v-model="exporting" :title="$t('exportingData')" @close="close" no-close-on-esc no-close-on-backdrop
             no-enforce-focus ok-only ok-title="Cancel" @ok="close">
      <div class="alert alert-warning">
        <span>{{ $t('exportNotice') }}</span>
      </div>
      <b-progress :max="totalCount">
        <b-progress-bar :value="downloadedCount"
                        :label="`${((downloadedCount / totalCount) * 100).toFixed(0)}%`"></b-progress-bar>
      </b-progress>
    </b-modal>
  </button>
</template>

<script>
import {saveAs} from 'file-saver';
import Excel from 'exceljs';
import moment from 'moment';

export default {
  props: ['filter', 'totalCount'],
  data() {
    return {
      exporting: false,
      cancelRequest: false,
      downloadedCount: 0
    }
  },
  methods: {
    calcBlockTime(confirmTime) {
      return 30 - moment().diff(moment(confirmTime), 'days');
    },
    getStatus(item) {
      if (item.cancelTime) return this.$t('canceled');
      if (item.sendContractTime) return this.$t('sentContract');
      if (item.confirmTime) return this.$t('confirmed');
      return this.$t('waiting');
    },
    getBlockTime(item) {
      if (!item.sendContractTime && item.confirmTime) {
        const daysLeft = this.calcBlockTime(item.confirmTime);
        return daysLeft > 0 ? `${daysLeft} ngày` : 'Đã quá 30 ngày';
      }
      return this.$t('no');
    },
    exportClientData: async function () {
      this.downloadedCount = 0;
      this.exporting = true;
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('data');
      this.setupSheet(sheet);

      let response = null;
      let params = Object.assign({}, this.filter);
      params.page = 1;
      params.pageSize = 1000;
      do {
        if (this.cancelRequest) {
          break;
        }
        let result = await this.$service.get('/client-kyc', {
          params: params
        })
        params.page += 1;
        response = result.data;
        response.docs.forEach(item => {
          let row = {
            companyName: item.client ? item.client.companyName : '',
            domain: item.client ? item.client.domain : '',
            status: this.getStatus(item),
            blocked: this.getBlockTime(item),
            sales: item.sales && item.sales.name ? item.sales.name : '',
            account: item.account && item.account.name ? item.account.name : '',
            createTime: moment(item.createTime).format('DD/MM/YYYY HH:mm:ss'),
            updateTime: moment(item.updateTime).isValid() ? moment(item.updateTime).format('DD/MM/YYYY HH:mm:ss') : '',
            updateBy: item.updatedBy && item.updatedBy.name ? item.updatedBy.name : '',
            confirmedBy: item.confirmedBy && item.confirmedBy.name ? item.confirmedBy.name : '',
            canceledBy: item.canceledBy && item.canceledBy.name ? item.canceledBy.name : '',
            sentContractBy: item.sentContractBy && item.sentContractBy.name ? item.sentContractBy.name : '',
            confirmTime: item.confirmTime ? moment(item.confirmTime).format('DD/MM/YYYY HH:mm:ss') : '',
            cancelTime: item.cancelTime ? moment(item.cancelTime).format('DD/MM/YYYY HH:mm:ss') : '',
            sendContractTime: item.sendContractTime ? moment(item.sendContractTime).format('DD/MM/YYYY HH:mm:ss') : '',
          }
          sheet.addRow(row);
          this.downloadedCount += 1;
        })
      } while (response && response.hasNextPage);
      if (!this.cancelRequest) {
        workbook.xlsx.writeBuffer({base64: true}).then(data => {
          this.exporting = false;
          saveAs(new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'client-kyc.xlsx');
        }).finally(() => {
          this.cancelRequest = false;
          this.downloadedCount = 0;
        });
      }
    },
    setupSheet: function (sheet) {
      sheet.columns = [
        {header: this.$t('companyName'), key: 'companyName', width: 20},
        {header: this.$t('domain'), key: 'domain', width: 40},
        {header: this.$t('status'), key: 'status', width: 20},
        {header: this.$t('blocked'), key: 'blocked', width: 20},
        {header: this.$t('sales'), key: 'sales', width: 20},
        {header: this.$t('account'), key: 'account', width: 20},
        {header: this.$t('createTime'), key: 'createTime', width: 20},
        {header: this.$t('updateTime'), key: 'updateTime', width: 20},
        {header: this.$t('updateBy'), key: 'updateBy', width: 20},
        {header: this.$t('confirmedBy'), key: 'confirmedBy', width: 20},
        {header: this.$t('canceledBy'), key: 'canceledBy', width: 20},
        {header: this.$t('sentContractBy'), key: 'sentContractBy', width: 20},
        {header: this.$t('confirmTime'), key: 'confirmTime', width: 20},
        {header: this.$t('cancelTime'), key: 'cancelTime', width: 20},
        {header: this.$t('sendContractTime'), key: 'sendContractTime', width: 20},
      ]
    },
    close() {
      this.cancelRequest = true;
    }
  },
  i18n: {
    messages: {
      en: {
        companyName: 'Company name',
        blocked: 'Blocked',
        updateBy: 'Update by',
        no: 'No',
        canceled: 'Canceled',
        confirmed: 'Confirmed',
        sendContract: 'Send contract',
        account: 'Account',
        domain: 'Domain',
        confirmedBy: 'Confirmed by',
        canceledBy: 'Canceled by',
        sentContractBy: 'Sent contract by',
        confirmTime: 'Confirm time',
        cancelTime: 'Cancel time',
        sendContractTime: 'Send contract time',
      },
      vi: {
        companyName: 'Tên công ty',
        blocked: 'Bị khóa',
        updateBy: 'Người cập nhật',
        no: 'Không',
        canceled: "Bị từ chối",
        confirmed: "Đã xác nhận",
        sentContract: "Hoàn thành hợp đồng",
        account: "Chăm sóc",
        domain: "Tên miền",
        confirmedBy: "Người xác nhận",
        canceledBy: "Người từ chối",
        sentContractBy: "Người gửi hợp đồng",
        confirmTime: "Xác nhận lúc",
        cancelTime: "Từ chối lúc",
        sendContractTime: "Gửi hợp đồng lúc",
      }
    }
  }
}
</script>